
import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { addDays, format } from "date-fns";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLanguage } from "@/contexts/LanguageContext";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

interface DateRangePickerProps {
  className?: string;
  initialDateFrom?: Date;
  initialDateTo?: Date;
  onUpdate?: (date: { from: Date | undefined; to: Date | undefined }) => void;
  children?: React.ReactNode;
}

export function DateRangePicker({
  className,
  initialDateFrom,
  initialDateTo,
  onUpdate,
  children,
}: DateRangePickerProps) {
  const { t } = useLanguage();
  const [date, setDate] = React.useState<DateRange | undefined>({
    from: initialDateFrom,
    to: initialDateTo,
  });

  const handleDateChange = (newDate: DateRange | undefined) => {
    setDate(newDate);
    // Call onUpdate even when only the 'from' date is set
    if (newDate?.from && onUpdate) {
      onUpdate({ 
        from: newDate.from, 
        to: newDate.to // This can be undefined for single-day selection
      });
    }
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          {children || (
            <Button
              id="date"
              variant={"outline"}
              className={cn(
                "w-[300px] justify-start text-left font-normal",
                !date && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date?.from ? (
                date.to ? (
                  <>
                    {format(date.from, "LLL dd, y")} -{" "}
                    {format(date.to, "LLL dd, y")}
                  </>
                ) : (
                  format(date.from, "LLL dd, y")
                )
              ) : (
                <span>{t("pickADate") || "Pick a date"}</span>
              )}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <ScrollArea className="h-80 max-w-[90vw]">
            <ScrollBar orientation="horizontal" />
            <div className="flex p-2">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                onSelect={handleDateChange}
                numberOfMonths={3}
                classNames={{
                  months: "flex flex-row space-x-4 space-y-0",
                  month: "space-y-4",
                  head_row: "flex",
                  row: "flex w-full mt-2",
                  day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md",
                  cell: "text-center text-sm p-0 relative h-9 w-9 focus-within:relative focus-within:z-20",
                }}
              />
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
}

